import type { SwiperOptions } from 'swiper';
import { FreeMode } from 'swiper';
import {
  isFirstDayOfMonth,
  parseISO,
  differenceInDays,
  addDays,
} from 'date-fns';
import { formatDate } from '@lt/components/utils/date';
import { dataLayerEvents, dataLayerPush } from 'src/helpers/gtm';
import { formatServerDate } from 'src/utils/dateUtils';
import type { DateYMDString } from '@lt/api/dist/types/date';

import type { Data } from './types';
import type { PropsFromRedux } from '.';
import { COUNT } from '../../store/priceChart/constants';

/**
 * Отправляет событие chart_hovered в gtm один раз
 */
export const passChartHoveredEvent = () => {
  const event = dataLayerEvents('chart_hovered');
  if (event.length >= 1) {
    return;
  }

  dataLayerPush({ event: 'chart_hovered' });
};

export const getHighestAmount = (data: Data[]) => {
  const amounts = data.map((item) => item.full_price || 0);

  return Math.max(...amounts);
};

/**
 * Показываем все даты с днями недели кроме 1 дня месяца
 * @return 2 ср, 3 чт, 4 пт, 5 сб, 6 вс, 7 пн
 *
 * В первый день месяца, выводим 1 и название месяца
 * @return 1 окт, 1 ноя, 1 дек
 *
 * C флагом isShort выводим только цифру
 * @return 1, 2, 3
 */
export const getColumnDescription = (date: Date, isShort = false) => {
  if (isShort) return formatDate(date, 'd');

  if (isFirstDayOfMonth(date)) {
    return formatDate(date, 'd MMM');
  }

  return formatDate(date, 'd EEEEEE');
};

export const getSearchDateIndex = (
  data: Data[],
  searchDate: PropsFromRedux['searchDate'],
): number => {
  const index = data.findIndex((item) => item.start_date === searchDate);
  return index;
};

export const getInitialParams = (
  activeIndex: number,
  isMobile: PropsFromRedux['isMobile'],
) => {
  const initialParams: SwiperOptions = {
    modules: [FreeMode],
    freeMode: true,
    slidesPerView: 'auto',
    slidesPerGroupAuto: true,
    watchSlidesProgress: true,
    runCallbacksOnInit: false,
    centeredSlides: true,
  };

  return {
    ...initialParams,
    allowTouchMove: isMobile,
    initialSlide: activeIndex,
  };
};

/**
 * Получаем изначальную дату для выполнения запроса
 *
 * Правила расчет диапазона:
 * 1. дата начала тура - сегодняшняя дата >= 15, запрашиваем 15 дней назад от даты начала тура и 15 дней вперед от даты начала
 * 2. дата начала тура - сегодняшняя дата < 15, то мы делаем запрос на 31 день от сегодня
 */
export const getInitialStartDate = (tourStartDate: string) => {
  const today = new Date();
  const tourDate = parseISO(tourStartDate);
  const daysDiff = differenceInDays(tourDate, today);

  const startDateForFetching =
    daysDiff >= 15 ? tourDate : addDays(today, COUNT / 2);

  return formatServerDate(startDateForFetching) as DateYMDString;
};
